import {Auth, onIdTokenChanged, User} from 'firebase/auth';
import {useEffect, useState} from 'react';
import {FuPaUser} from './authTypes';
import * as Sentry from '@sentry/react';
import {setDimensionToGA4} from '../service/gtagHelpers';

type IdTokenHook = [User | null, FuPaUser | null, boolean, Error | null];

const useIdToken = (auth: Auth): IdTokenHook => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [fupaUser, setFupaUser] = useState<FuPaUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const handleError = (error: Error) => {
    setIsLoading(false);
    setError(error);
  };

  useEffect(() => {
    const listener = onIdTokenChanged(
      auth,
      async (user) => {
        if (user) {
          // Get IdToken decoded, with claims (including emailVerified) and token base64 encoded
          try {
            const decodedIdToken = await user.getIdTokenResult();
            setFirebaseUser(user);
            setFupaUser(decodedIdToken.claims);
            setDimensionToGA4({user_id: user.uid});
            Sentry.setUser({id: user.uid});
          } catch (e) {
            if (e instanceof Error) {
              setError(e);
            }
          } finally {
            setIsLoading(false);
          }
        } else {
          setFirebaseUser(null);
          setFupaUser(null);
          setIsLoading(false);
        }
      },
      handleError
    );

    return () => {
      listener();
    };
  }, [auth]);

  return [firebaseUser, fupaUser, isLoading, error];
};

export default useIdToken;
